import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Trash2 } from 'lucide-react';
import { DeadlineSection, RepeatSection } from './AddInterestDialog';

export const EditInterestDialog = ({ open, onOpenChange, interest, onChange, onSubmit, onDelete }) => {
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);
  const navigate = useNavigate();

  const handleOpenChange = (isOpen) => {
    onOpenChange(isOpen);
    if (!isOpen) setShowDeleteWarning(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-['Manrope']">Edit Interest</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="edit-title">Title</Label>
            <Input
              id="edit-title"
              value={interest?.title || ''}
              onChange={(e) => onChange({ ...interest, title: e.target.value })}
              data-testid="edit-interest-title-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-description">Description</Label>
            <Textarea
              id="edit-description"
              value={interest?.description || ''}
              onChange={(e) => onChange({ ...interest, description: e.target.value })}
              rows={2}
              data-testid="edit-interest-description-input"
            />
          </div>
          
          <DeadlineSection interest={interest} onChange={onChange} prefix="edit-" />
          <RepeatSection interest={interest} onChange={onChange} prefix="edit-" />
        </div>
        <DialogFooter className="flex-col gap-3 sm:flex-col">
          {!showDeleteWarning ? (
            <div className="flex justify-between w-full">
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive hover:bg-destructive/10 gap-1.5"
                onClick={() => setShowDeleteWarning(true)}
                data-testid="edit-delete-btn"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Delete
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => handleOpenChange(false)}>Cancel</Button>
                <Button onClick={onSubmit} data-testid="update-interest-btn">Save Changes</Button>
              </div>
            </div>
          ) : (
            <div className="w-full space-y-3 p-3 rounded-lg border border-destructive/30 bg-destructive/5">
              <p className="text-sm leading-relaxed">
                Are you sure? If you have achieved anything regarding this interest, select the green tick to complete it. Even recognising that this interest isn't worth pursuing is an achievement, as you have gained attention which you can focus elsewhere. Generally, only delete an interest if it's a duplicate or a mistake. Deleted interests won't be recorded in your list of{' '}
                <a
                  href="/achievements"
                  onClick={(e) => { e.preventDefault(); handleOpenChange(false); navigate('/achievements'); }}
                  className="text-primary hover:underline font-medium"
                >achievements</a>.
              </p>
              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDeleteWarning(false)}
                  data-testid="cancel-delete-interest-btn"
                >
                  Go Back
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => {
                    onDelete(interest.interest_id);
                    handleOpenChange(false);
                  }}
                  data-testid="confirm-delete-interest-btn"
                >
                  Delete Interest
                </Button>
              </div>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
